import {useCallback,  useRef} from 'react'
import moment from 'moment'
import useApi from '../../../services/useApi'
import useFilters from '../../../hook/useFilters'
import {MONTHS_SHORT} from '../../../utils/date'
import {ENTITY_PERFORMANCE_CHART_VIEW_OPTIONS} from '../utils'
import {generateColorsFromIndex} from '../../../utils/chartColors'
import {ReportType} from '../interfaces'

const ENTITY_PARAM_MAP: Record<ReportType, string> = {
  customer_group: 'customer_group_id',
  customer_type: 'customer_type',
  sales_rep: 'rep_email',
  sales_rep_type: 'sales_rep_type',
  coupon_code: 'coupon_code',
}

const useGetEntityPerformanceChartData = (reportType: ReportType) => {
  const Api = useApi()
  const lastEntityValue = useRef<string | null>(null)

  const initialFilters = {
    start_date: moment().subtract(6, 'days').format('YYYY-MM-DD'),
    end_date: moment().format('YYYY-MM-DD'),
    period: 'day',
    report_type: reportType,
    [ENTITY_PARAM_MAP[reportType]]: '',
  }

  const {filters, setSingleFilter, setMultipleFilters} = useFilters(initialFilters)

  const {data: response, isFetching: isLoading} = Api.useGetQuery(
    '/analytics/coupon-discount/charts',
    {
      queryId: `coupon-discount-${reportType}-${filters?.period}`,
      filters: filters,
      isToast: false,
      isErrorPageRedirection: false,
    },
    {
      enabled: !!filters?.[ENTITY_PARAM_MAP[reportType]],
    }
  )

  const entityPerformanceChartData = (() => {
    if (!response?.data?.datasets?.length) {
      return {
        labels: [],
        datasets: [
          {
            label: '',
            data: [],
            borderColor: generateColorsFromIndex(0),
            backgroundColor: generateColorsFromIndex(0),
            fill: false,
          },
        ],
      }
    }

    let {labels, datasets} = response.data
    const {coupon_discount_data, label} = datasets[0] || {}

    let newLabels = []

    if (filters.period === 'month') {
      labels.sort((a: any, b: any) => {
        const [aMonth, aYear] = a.split('_').map(Number)
        const [bMonth, bYear] = b.split('_').map(Number)
        return aYear !== bYear ? aYear - bYear : aMonth - bMonth
      })

      newLabels = labels.map((label: any) => {
        const [month, year] = label.split('_').map(Number)
        return `${MONTHS_SHORT[month - 1]} ${year}`
      })
    } else if (filters.period === 'day') {
      newLabels = labels.map((label: any) => label.split('_')[0])
    } else {
      newLabels = labels
    }

    return {
      labels: newLabels,
      datasets: [
        {
          label: label,
          data: coupon_discount_data,
          borderColor: generateColorsFromIndex(0),
          backgroundColor: generateColorsFromIndex(0),
          fill: false,
        },
      ],
    }
  })()

  const onEntityPerformanceChartDateRangeChange = (startDate: any, endDate: any) => {
    setMultipleFilters({
      start_date: startDate?.format('YYYY-MM-DD'),
      end_date: endDate?.format('YYYY-MM-DD'),
      period: ENTITY_PERFORMANCE_CHART_VIEW_OPTIONS(
        startDate?.format('YYYY-MM-DD'),
        endDate?.format('YYYY-MM-DD')
      )[0].value,
    })
  }

  const onEntityPerformanceChartIdChange = useCallback(
    (entityValue: string) => {
      lastEntityValue.current = entityValue
      setMultipleFilters({
        [ENTITY_PARAM_MAP[reportType]]: entityValue,
      })
    },
    [reportType, setMultipleFilters]
  )

  const onEntityPerformanceChartViewChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSingleFilter('period', e.target.value)
  }

  return {
    entityPerformanceChartData,
    isLoading,
    onEntityPerformanceChartIdChange,
    onEntityPerformanceChartViewChange,
    onEntityPerformanceChartDateRangeChange,
    entityPerformanceChartFilters: filters,
  }
}

export default useGetEntityPerformanceChartData
